import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Seo } from "../components/Seo";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, ChevronUp, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { CATALOG_PDF, VG_ORANGE } from "../lib/brand";

const TOTAL_PAGES = 96;
const pageSrc = (n) => `/docs/catalog-pages/page-${String(n).padStart(3, "0")}.webp`;
const PAGES = Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1);

// Table of contents — section label + starting page.
const SECTIONS = [
  { label: "Company History", page: 4 },
  { label: "LEED Credits", page: 5 },
  { label: "Zone Systems", page: 6 },
  { label: "Sheet / Roll Goods", page: 8 },
  { label: "Carpet Tile", page: 20 },
  { label: "Custom Logo Matting", page: 37 },
  { label: "Commercial Mats", page: 45 },
  { label: "Luxury Vinyl Planks & Tile", page: 48 },
  { label: "Grid Entry Systems", page: 55 },
  { label: "Industrial & Anti-Fatigue Mats", page: 68 },
  { label: "Non-Slip Flooring", page: 76 },
  { label: "Recycled Rubber Flooring", page: 78 },
  { label: "Adhesives & Accessories", page: 87 },
  { label: "Metal Transitions", page: 90 },
  { label: "General Maintenance", page: 93 },
  { label: "Index", page: 95 },
];

const snippet = (text, q) => {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text.slice(0, 90) + "…";
  const start = Math.max(0, i - 35);
  return (start > 0 ? "…" : "") + text.slice(start, i + q.length + 55).trim() + "…";
};

export default function CatalogViewer() {
  const [current, setCurrent] = useState(1);
  const [showTop, setShowTop] = useState(false);
  const [index, setIndex] = useState([]);
  const [query, setQuery] = useState("");
  const [jump, setJump] = useState("");
  const [offset, setOffset] = useState(64); // top offset below site header
  const [scrollPad, setScrollPad] = useState(260); // scroll-margin so pages land below bars
  const pageRefs = useRef({});
  const toolbarRef = useRef(null);
  const chipRefs = useRef({});

  useEffect(() => {
    fetch("/docs/catalog-index.json").then((r) => r.json()).then(setIndex).catch(() => setIndex([]));
  }, []);

  // Measure the site header so the catalog toolbar sits right below it (no overlap).
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      const h = header ? header.offsetHeight : 0;
      const tb = toolbarRef.current ? toolbarRef.current.offsetHeight : 60;
      setOffset(h);
      setScrollPad(h + tb + 16);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [index]);

  // Track visible page for the counter + active section.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const n = Number(entry.target.dataset.page);
            if (n) setCurrent(n);
          }
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    Object.values(pageRefs.current).forEach((el) => el && observer.observe(el));
    const onScroll = () => {
      setShowTop(window.scrollY > 800);
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 12) setCurrent(TOTAL_PAGES);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goToPage = useCallback((n) => {
    const target = Math.min(TOTAL_PAGES, Math.max(1, Number(n) || 1));
    const el = pageRefs.current[target];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Active section = last section whose start page <= current.
  const activeSection = useMemo(() => {
    let active = SECTIONS[0]?.page;
    for (const s of SECTIONS) if (current >= s.page) active = s.page;
    return active;
  }, [current]);

  // Keep the active section chip in view within the horizontal bar.
  useEffect(() => {
    const el = chipRefs.current[activeSection];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeSection]);

  const results = useMemo(() => {
    const q = query.trim();
    if (q.length < 2 || !index.length) return [];
    return index
      .filter((e) => e.text.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 12)
      .map((e) => ({ page: e.page, snip: snippet(e.text, q) }));
  }, [query, index]);

  return (
    <div className="bg-neutral-100" data-testid="catalog-viewer">
      <Seo title="Digital Catalog" description="Browse and search the complete van Gelder, Inc. commercial flooring digital catalog online — jump straight to any section." />

      {/* Sticky toolbar (offset below the site header) */}
      <div ref={toolbarRef} className="sticky z-40 border-b border-neutral-200 bg-white/95 backdrop-blur" style={{ top: offset }}>
        <div className="mx-auto max-w-5xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-[#ED8B00] transition-colors" data-testid="catalog-back-link">
                <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Products</span>
              </Link>
              <span className="hidden h-4 w-px bg-neutral-300 sm:block" />
              <h1 className="font-heading text-base sm:text-lg font-extrabold text-neutral-900">Digital Catalog</h1>
            </div>

            {/* Search */}
            <div className="relative order-3 w-full sm:order-2 sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products or text…" data-testid="catalog-search-input"
                className="w-full rounded-full border border-neutral-300 bg-white py-2 pl-9 pr-8 text-sm focus:border-[#ED8B00] focus:outline-none focus:ring-1 focus:ring-[#ED8B00]"
              />
              {query && (
                <button onClick={() => setQuery("")} aria-label="Clear search" data-testid="catalog-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
                  <X className="h-4 w-4" />
                </button>
              )}
              {query.trim().length >= 2 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-xl" data-testid="catalog-search-results">
                  {results.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-neutral-500">No matches found.</p>
                  ) : (
                    results.map((r) => (
                      <button key={r.page} onClick={() => { goToPage(r.page); setQuery(""); }} data-testid={`catalog-search-result-${r.page}`} className="flex w-full items-start gap-3 border-b border-neutral-100 px-4 py-2.5 text-left last:border-0 hover:bg-orange-50">
                        <span className="mt-0.5 rounded px-2 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: VG_ORANGE }}>p.{r.page}</span>
                        <span className="text-xs leading-snug text-neutral-600">{r.snip}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Pagination controls */}
            <div className="order-2 flex items-center gap-2 sm:order-3">
              <span className="hidden text-sm font-medium text-neutral-500 md:inline" data-testid="catalog-page-indicator">Page {current} / {TOTAL_PAGES}</span>
              <button onClick={() => goToPage(current - 1)} disabled={current <= 1} aria-label="Previous page" data-testid="catalog-prev" className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 text-neutral-600 transition-colors hover:border-[#ED8B00] hover:text-[#ED8B00] disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <form onSubmit={(e) => { e.preventDefault(); goToPage(jump); setJump(""); }} className="flex items-center gap-1 text-sm text-neutral-500">
                <input type="number" min={1} max={TOTAL_PAGES} value={jump} onChange={(e) => setJump(e.target.value)} placeholder={String(current)} aria-label="Go to page" data-testid="catalog-jump-input" className="w-14 rounded-md border border-neutral-300 px-2 py-1.5 text-center text-sm focus:border-[#ED8B00] focus:outline-none focus:ring-1 focus:ring-[#ED8B00]" />
                <span className="whitespace-nowrap">/ {TOTAL_PAGES}</span>
              </form>
              <button onClick={() => goToPage(current + 1)} disabled={current >= TOTAL_PAGES} aria-label="Next page" data-testid="catalog-next" className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 text-neutral-600 transition-colors hover:border-[#ED8B00] hover:text-[#ED8B00] disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
              <Button asChild variant="outline" size="sm" className="ml-1 border-[#ED8B00] text-[#ED8B00] hover:bg-orange-50" data-testid="catalog-viewer-download">
                <a href={CATALOG_PDF} download><Download className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">PDF</span></a>
              </Button>
            </div>
          </div>

          {/* Section (Table of Contents) bar */}
          <div className="-mx-4 mt-3 overflow-x-auto border-t border-neutral-100 px-4 pt-2.5" data-testid="catalog-sections">
            <div className="flex items-center gap-2 whitespace-nowrap pb-0.5">
              {SECTIONS.map((s) => {
                const active = activeSection === s.page;
                return (
                  <button
                    key={s.page}
                    ref={(el) => (chipRefs.current[s.page] = el)}
                    onClick={() => goToPage(s.page)}
                    data-testid={`catalog-section-${s.page}`}
                    className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      active
                        ? "border-transparent text-white"
                        : "border-neutral-300 text-neutral-600 hover:border-[#ED8B00] hover:text-[#ED8B00]"
                    }`}
                    style={active ? { backgroundColor: VG_ORANGE } : undefined}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Page reader (continuous scroll, lazy-loaded) */}
      <div className="mx-auto max-w-4xl px-2 py-6 sm:px-4 sm:py-8">
        <div className="flex flex-col items-center gap-4 sm:gap-6">
          {PAGES.map((n) => (
            <img
              key={n}
              ref={(el) => (pageRefs.current[n] = el)}
              data-page={n}
              src={pageSrc(n)}
              alt={`van Gelder catalog page ${n}`}
              loading={n <= 2 ? "eager" : "lazy"}
              style={{ scrollMarginTop: scrollPad }}
              className="w-full max-w-3xl rounded-md bg-white shadow-md ring-1 ring-neutral-200"
              data-testid={`catalog-page-${n}`}
            />
          ))}
        </div>
        <div className="mt-8 text-center text-sm text-neutral-500">
          That's the full catalog.{" "}
          <a href={CATALOG_PDF} download className="font-semibold" style={{ color: VG_ORANGE }}>Download the PDF</a> to keep a copy.
        </div>
      </div>

      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="catalog-back-to-top" aria-label="Back to top" className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105" style={{ backgroundColor: VG_ORANGE }}>
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
